import { useMemo } from 'react';
import { Input } from 'rsuite';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import {
  RESUME_SECTIONS,
  VARIATION_GROUPS,
  PLACEHOLDER_BY_KEY,
  PLACEHOLDER_LABELS,
  composeResumeText,
  readResumeSections,
  safeParseVariations,
} from './engine';
import { styles } from './styles';

// Insere um token {{key}} no cursor de um input/textarea por id, com espaçamento
// automático se colar em outra chave.
function insertAtCursor(elementId, token, apply) {
  const input = document.getElementById(elementId);
  if (!input) {
    apply(`${token}`);
    return;
  }
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  const before = input.value[start - 1];
  const after = input.value[end];
  const sepBefore = before === '}' ? ' ' : '';
  const sepAfter = after === '{' ? ' ' : '';
  const insert = sepBefore + token + sepAfter;
  const next = input.value.substring(0, start) + insert + input.value.substring(end);
  apply(next);
  requestAnimationFrame(() => {
    input.focus();
    const pos = start + insert.length;
    input.selectionStart = pos;
    input.selectionEnd = pos;
  });
}

// Editor da mensagem de resumo do pedido: seções nomeadas (o sistema decide
// quando mostrar via condicionais) + variações avançadas de formatação.
export default function ResumeEditor({ answer, onChangeAnswer }) {
  const { theme } = useCustomTheme();
  const sections = useMemo(() => readResumeSections(answer.variations), [answer.variations]);
  const variations = useMemo(() => safeParseVariations(answer.variations), [answer.variations]);

  const updateSection = (key, value) => {
    const nextSections = { ...sections, [key]: value };
    const nextVariations = { ...variations, sections: nextSections };
    onChangeAnswer({ text: composeResumeText(nextSections), variations: JSON.stringify(nextVariations) });
  };

  const setVariation = (key, value) => {
    onChangeAnswer({ variations: JSON.stringify({ ...variations, [key]: value }) });
  };

  const setVariationField = (key, subKey, value) => {
    onChangeAnswer({ variations: JSON.stringify({ ...variations, [key]: { ...(variations[key] ?? {}), [subKey]: value } }) });
  };

  const renderVariationField = (field) => {
    if (field.type === 'object') {
      const sub = variations[field.key] ?? {};
      return (
        <div style={{ marginBottom: 14 }} key={field.key}>
          <label style={styles.fieldLabel(theme)}>{field.label}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            {field.fields.map((f) => (
              <div key={f.key}>
                <span style={{ fontSize: 11.5, opacity: 0.7, color: theme.colors.text }}>{f.label}</span>
                <Input size="sm" value={sub[f.key] ?? ''} onChange={(v) => setVariationField(field.key, f.key, v)} />
              </div>
            ))}
          </div>
        </div>
      );
    }
    const value = variations[field.key] ?? '';
    const id = `variation-${field.key}`;
    return (
      <div style={{ marginBottom: 14 }} key={field.key}>
        <label style={styles.fieldLabel(theme)}>{field.label}</label>
        {field.description && <p style={styles.fieldHint(theme)}>{field.description}</p>}
        {field.example && (
          <p style={{ fontSize: 11.5, margin: '0 0 6px' }}>
            <span style={{ opacity: 0.6, color: theme.colors.text }}>Aparece como: </span>
            <code style={{ background: theme.colors.backgroundSecondary, padding: '1px 5px', borderRadius: 4 }}>{field.example}</code>
          </p>
        )}
        {field.chips?.length > 0 && (
          <div style={styles.chips}>
            {field.chips.map((c) => (
              <button type="button" key={c} style={styles.chip(theme)} onClick={() => insertAtCursor(id, `{{${c}}}`, (v) => setVariation(field.key, v))}>
                + {PLACEHOLDER_LABELS[c] ?? c}
              </button>
            ))}
          </div>
        )}
        <Input id={id} as={field.type === 'textarea' ? 'textarea' : 'input'} rows={field.rows ?? 2} size={field.type === 'textarea' ? undefined : 'sm'} value={value} onChange={(v) => setVariation(field.key, v)} style={{ marginTop: 6 }} />
      </div>
    );
  };

  return (
    <div>
      <label style={styles.fieldLabel(theme)}>Estrutura da mensagem</label>
      <p style={styles.fieldHint(theme)}>
        Edite cada bloco no campo correspondente. Blocos condicionais aparecem só quando a situação do pedido bater
        com a regra (ex.: o bloco de troco só aparece em pedidos pagos em dinheiro com troco). Para esconder um bloco,
        deixe o campo vazio.
      </p>

      {RESUME_SECTIONS.map((s) => (
        <div style={styles.resumeSection(theme)} key={s.key}>
          <div style={styles.resumeSectionHead}>
            <div style={styles.resumeSectionTitle(theme)}><span>{s.icon}</span> {s.title}</div>
            <span style={styles.resumeBadge(theme, !!s.condition)}>{s.condition ? 'condicional' : 'sempre aparece'}</span>
          </div>
          <p style={styles.resumeSectionDesc(theme)}>{s.description}</p>
          <Input id={`section-${s.key}`} as="textarea" rows={s.key === 'itens' ? 5 : 2} value={sections[s.key] ?? ''} onChange={(v) => updateSection(s.key, v)} />
          {s.placeholders.length > 0 && (
            <div style={styles.chips}>
              {s.placeholders.map((pk) => (
                <button type="button" key={pk} style={styles.chip(theme)} onClick={() => insertAtCursor(`section-${s.key}`, `{{${pk}}}`, (v) => updateSection(s.key, v))}>
                  + {PLACEHOLDER_BY_KEY[pk]?.label ?? pk}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}

      <details style={{ marginTop: 14 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: theme.colors.text }}>Formatação avançada dos itens, endereço e rótulos</summary>
        <div style={{ marginTop: 12 }}>
          {VARIATION_GROUPS.map((group) => (
            <div key={group.key} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text, marginBottom: 2 }}>{group.icon} {group.title}</div>
              <p style={styles.fieldHint(theme)}>{group.description}</p>
              {group.fields.map(renderVariationField)}
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}
